import {
  Building2,
  Briefcase,
  IndianRupee,
  Wallet,
  BadgePercent,
  Boxes,
  Receipt,
  Landmark,
  TrendingUp,
  ShieldCheck,
  Clock3,
  CircleDollarSign,
  User,
  Users,
  FileText,
} from "./icons";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "CC Limit",
      path: "/cc-limit",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Cash Credit (CC) Limit | Working Capital Finance | Money Mart",

  description:
    "Get a Cash Credit (CC) Limit from leading Banks & NBFCs to finance inventory, receivables and day-to-day working capital requirements. Expert assistance from Money Mart.",

  keywords:
    "Cash Credit Limit, CC Limit, Working Capital Loan, Working Capital Finance, Business Credit Line, Inventory Finance, Money Mart",

  canonical: "/cc-limit",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Working Capital Finance",

  title: "Cash Credit Limits for Growing Businesses",

  description:
    "A Cash Credit (CC) Limit provides businesses with a revolving working capital facility secured against stock and receivables. Maintain smooth operations, manage seasonal demand, and improve cash flow with funding from leading Banks & NBFCs.",

  highlights: [
    "Working Capital up to Approved Limit",
    "Ideal for Inventory & Receivables",
    "Interest on Utilized Amount",
    "Competitive Interest Rates",
  ],

  image: "/images/loans/cc_limit.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Cash Credit Facility",

  title: "Keep Your Business Running Without Cash Flow Interruptions",

  description:
    "A Cash Credit (CC) Limit is a revolving working capital facility that enables businesses to finance inventory, manage receivables, pay suppliers, and meet operational expenses without affecting daily business continuity.",

  items: [
    {
      icon: Boxes,
      title: "Inventory Financing",
      description:
        "Maintain adequate stock levels without blocking your working capital.",
    },
    {
      icon: Receipt,
      title: "Receivables Support",
      description:
        "Bridge the gap between raising invoices and receiving customer payments.",
    },
    {
      icon: Wallet,
      title: "Flexible Withdrawals",
      description:
        "Access funds whenever required within the sanctioned Cash Credit limit.",
    },
    {
      icon: TrendingUp,
      title: "Healthy Business Cash Flow",
      description:
        "Meet operational expenses while maintaining smooth day-to-day business operations.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Businesses Prefer Cash Credit Limits",

  description:
    "Cash Credit facilities offer continuous access to working capital while helping businesses efficiently manage inventory, receivables and operational expenses.",

  items: [
    {
      icon: CircleDollarSign,
      title: "Interest on Utilized Amount",
      description:
        "Pay interest only on the amount actually utilized instead of the entire sanctioned limit.",
    },
    {
      icon: Boxes,
      title: "Finance Inventory",
      description:
        "Purchase raw materials and maintain inventory without affecting business liquidity.",
    },
    {
      icon: Receipt,
      title: "Manage Receivables",
      description:
        "Maintain uninterrupted operations while waiting for customer payments.",
    },
    {
      icon: Wallet,
      title: "Continuous Working Capital",
      description:
        "Withdraw and repay funds as per your business requirements within the approved limit.",
    },
    {
      icon: Clock3,
      title: "Meet Seasonal Demand",
      description:
        "Handle peak business seasons and temporary cash flow gaps with ease.",
    },
    {
      icon: ShieldCheck,
      title: "Business Continuity",
      description:
        "Ensure timely supplier payments, salaries and operating expenses without disrupting business operations.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Cash Credit Solutions",

  title: "CC Limit Solutions We Help Arrange",

  description:
    "Money Mart assists businesses in obtaining Cash Credit facilities tailored to their working capital requirements through leading Banks and NBFCs.",

  items: [
    {
      icon: Landmark,
      title: "Standard Cash Credit Limit",
      description:
        "Working capital finance secured against stock, receivables and other eligible business assets.",
    },
    {
      icon: Boxes,
      title: "Inventory-Based CC",
      description:
        "Credit limits designed primarily to finance inventory and raw material purchases.",
    },
    {
      icon: Receipt,
      title: "Receivables-Based CC",
      description:
        "Funding against outstanding invoices and book debts to improve business liquidity.",
    },
    {
      icon: Building2,
      title: "Working Capital CC",
      description:
        "Customized Cash Credit facilities to support daily business operations and expansion.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Cash Credit Limit Eligibility",

  description:
    "Cash Credit eligibility is primarily based on your business turnover, inventory levels, receivables, financial performance, and repayment capacity.",

  requirements: [
    {
      icon: User,
      title: "Applicant",
      value:
        "Proprietorship, Partnership, LLP, Private Limited Company or Self-Employed Professional",
    },
    {
      icon: Briefcase,
      title: "Business Vintage",
      value:
        "Operational Business with Stable Track Record (as per lender norms)",
    },
    {
      icon: IndianRupee,
      title: "Business Turnover",
      value: "Regular Business Income & Verifiable Financial Statements",
    },
    {
      icon: Boxes,
      title: "Primary Security",
      value: "Stock, Inventory & Book Debts (subject to lender policy)",
    },
  ],

  approvalFactors: [
    "Annual business turnover",
    "Inventory holding levels",
    "Book debts and receivables",
    "Business profitability",
    "Credit score & repayment history",
    "Existing banking relationship",
    "Working capital requirement",
  ],

  cta: {
    title: "Want to know your eligible CC Limit?",

    description:
      "Our experts will evaluate your financials and help determine the most suitable Cash Credit limit for your business.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for Cash Credit (CC) Limit",

  description:
    "The exact documents vary across lenders. These are the documents generally required while applying for a Cash Credit facility.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: ["Passport Size Photograph", "PAN Card", "Aadhaar Card"],
    },
    {
      icon: Briefcase,
      title: "Business Documents",

      documents: [
        "GST Registration",
        "Business Registration Certificate",
        "UDYAM Registration (if applicable)",
        "Partnership Deed / LLP Agreement / MOA & AOA (as applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Financial Documents",

      documents: [
        "Last 3 Years Income Tax Returns",
        "Balance Sheet",
        "Profit & Loss Statement",
        "Latest Financial Statements",
      ],
    },
    {
      icon: Landmark,
      title: "Banking Documents",

      documents: [
        "Last 12 Months Business Bank Statements",
        "Existing Loan Details",
        "Sanction Letter of Existing Working Capital Facilities (if applicable)",
      ],
    },
    {
      icon: Boxes,
      title: "Stock & Receivables Details",

      documents: [
        "Latest Stock Statement",
        "Debtors / Creditors Statement",
        "Inventory Details",
        "Working Capital Assessment (if applicable)",
      ],
    },
    {
      icon: FileText,
      title: "Additional Supporting Documents",

      documents: [
        "Business Address Proof",
        "KYC of Proprietor / Partners / Directors",
        "Any Additional Documents Requested by the Lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get Your Cash Credit Limit in 6 Simple Steps",

  description:
    "From business assessment and financial evaluation to sanction and limit activation, Money Mart assists you throughout the entire Cash Credit application process.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Your Cash Credit Facility?",

  description:
    "Our experienced advisors help businesses secure the right Cash Credit facility by comparing multiple Banks and NBFCs, ensuring competitive interest rates, suitable limits, and a smooth application process.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Cash Credit (CC) Limit FAQs",

  description:
    "Find answers to the most commonly asked questions about Cash Credit facilities and working capital finance.",

  items: [
    {
      question: "What is a Cash Credit (CC) Limit?",

      answer:
        "A Cash Credit (CC) Limit is a revolving working capital facility that allows businesses to withdraw funds up to a sanctioned limit for day-to-day operational needs. Interest is charged only on the amount utilized.",
    },
    {
      question: "How is a Cash Credit Limit different from a Business Loan?",

      answer:
        "A Business Loan is typically disbursed as a lump sum with fixed EMIs, whereas a Cash Credit Limit is a revolving credit facility that can be utilized whenever required for working capital needs.",
    },
    {
      question: "What is the Cash Credit facility generally secured against?",

      answer:
        "Cash Credit facilities are commonly secured against inventory, stock, book debts, receivables, or other eligible business assets. The acceptable security depends on the lender's policy.",
    },
    {
      question: "Can I withdraw funds multiple times?",

      answer:
        "Yes. You can withdraw, repay, and reuse funds within your sanctioned Cash Credit limit, subject to the lender's terms and conditions.",
    },
    {
      question: "What can a Cash Credit Limit be used for?",

      answer:
        "Businesses commonly use Cash Credit facilities for purchasing inventory, paying suppliers, managing receivables, paying salaries, meeting operational expenses, and handling seasonal working capital requirements.",
    },
    {
      question: "How is the eligible CC Limit determined?",

      answer:
        "The sanctioned limit is based on factors such as business turnover, inventory levels, receivables, financial statements, banking history, repayment capacity, and the lender's credit assessment.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Need Working Capital?",

  title: "Get the Right Cash Credit Limit for Your Business",

  description:
    "Compare Cash Credit facilities from leading Banks & NBFCs with expert guidance from Money Mart and secure the working capital your business needs.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for CC Limit",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Working Capital Consultation",

  title: "Speak to a Cash Credit Expert",

  description:
    "Our business finance specialists will evaluate your working capital requirements and help you secure the most suitable Cash Credit facility from leading Banks & NBFCs.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our business finance expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Business Type",
      income: "Annual Turnover",
      loanAmount: "Required CC Limit",
    },

    incomeField: "annualTurnover",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25L",
        label: "Below ₹25 Lakhs",
      },
      {
        value: "25-100L",
        label: "₹25 Lakhs – ₹1 Crore",
      },
      {
        value: "1-5Cr",
        label: "₹1 Crore – ₹5 Crore",
      },
      {
        value: "5-10Cr",
        label: "₹5 Crore – ₹10 Crore",
      },
      {
        value: "10Cr-plus",
        label: "Above ₹10 Crore",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your business finance enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const ccLimit = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default ccLimit;
